/**
 * Party ledger page.
 *
 * Columns: Date | Description | Dr | Cr | Balance | Attach | Source.
 * Actions: Record payment, Record adjustment, Download PDF, Download PNG.
 * Toggle: show reversed entries.
 * Attachment viewer: click thumbnail → full-screen viewer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import type {
  EntryAttachment,
  LedgerListing,
  LedgerListingRow,
  Party,
  PartyBalance,
  Payment,
} from '../api';
import {
  ArrowDownToLine,
  BookOpen,
  FileText,
  ImageIcon,
  Paperclip,
  Pencil,
  PencilRuler,
  RotateCcw,
  Undo2,
} from 'lucide-react';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card, StatePill } from '../ui/components';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Spinner,
} from '../ui/primitives';
import { PaymentDialog } from '../payments/PaymentDialog';
import { AdjustmentDialog } from '../adjustments/AdjustmentDialog';
import { EditPartyDialog } from '../parties/EditPartyDialog';
import { usePinGate } from '../auth/pinGate';
import { fileToBase64 } from '../lib/file';

export function PartyLedgerPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const partyId = Number(idParam);
  const qc = useQueryClient();
  const readOnly = useReadOnly();

  const today = useMemo(() => todayIso(), []);

  const [from, setFrom] = useState(addDaysIso(today, -30));
  const [to, setTo] = useState(today);

  const openingBalanceQuery = useQuery<import('../api').OpeningBalance | null>({
    queryKey: ['opening-balance', partyId, today],
    queryFn: () =>
      api.get<import('../api').OpeningBalance | null>(
        `/api/opening-balances/${partyId}?asOf=${today}`,
      ).catch(() => null),
    enabled: Number.isFinite(partyId),
  });
  useEffect(() => {
    const ob = openingBalanceQuery.data;
    if (!ob) return;
    const smartFrom = addDaysIso(ob.asOfDate, 1);
    setFrom((prev) => (smartFrom > prev ? smartFrom : prev));
  }, [openingBalanceQuery.data]);

  const [includeReversed, setIncludeReversed] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [editPartyOpen, setEditPartyOpen] = useState(false);
  const [viewer, setViewer] = useState<{ entryId: number; index: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const party = useQuery<Party>({
    queryKey: ['party', partyId],
    queryFn: () => api.get<Party>(`/api/parties/${partyId}`),
    enabled: Number.isFinite(partyId),
  });
  const balance = useQuery<PartyBalance>({
    queryKey: ['balance', partyId],
    queryFn: () => api.get<PartyBalance>(`/api/parties/${partyId}/balance`),
    enabled: Number.isFinite(partyId),
  });
  const ledger = useQuery<LedgerListing>({
    queryKey: ['ledger', partyId, from, to, includeReversed],
    queryFn: () =>
      api.get<LedgerListing>(
        `/api/parties/${partyId}/ledger?from=${from}&to=${to}&include_reversed=${
          includeReversed ? 'true' : 'false'
        }`,
      ),
    enabled: Number.isFinite(partyId) && !!from && !!to,
  });

  const attachmentQueries = useQueries({
    queries: (ledger.data?.rows ?? []).map((r) => ({
      queryKey: ['entry-attachments', r.id],
      queryFn: () => api.get<EntryAttachment[]>(`/api/ledger-entries/${r.id}/attachments`),
    })),
  });
  const attachmentsByEntry = useMemo(() => {
    const m = new Map<number, EntryAttachment[]>();
    (ledger.data?.rows ?? []).forEach((r, i) => {
      const data = attachmentQueries[i]?.data;
      if (data) m.set(r.id, data);
    });
    return m;
  }, [ledger.data, attachmentQueries]);

  const voidPayMut = useMutation({
    mutationFn: async ({ paymentId, reason }: { paymentId: number; reason: string }) => {
      return api.post<Payment>(`/api/payments/${paymentId}/void`, { reason });
    },
    onSuccess: () => void qc.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const reverseEntryMut = useMutation({
    mutationFn: async ({ entryId, reason }: { entryId: number; reason: string }) =>
      api.post(`/api/ledger-entries/${entryId}/reverse`, { reason }),
    onSuccess: () => void qc.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const uploadAttachmentMut = useMutation({
    mutationFn: async ({ entryId, file }: { entryId: number; file: File }) => {
      const base64 = await fileToBase64(file);
      return api.post(`/api/ledger-entries/${entryId}/attachments`, {
        base64,
        originalFilename: file.name,
        mimeType: file.type || 'application/octet-stream',
      });
    },
    onSuccess: (_, { entryId }) => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments', entryId] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteAttachmentMut = useMutation({
    mutationFn: (attachmentId: number) =>
      api.delete(`/api/entry-attachments/${attachmentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const { withPin } = usePinGate();

  useEffect(() => {
    setError(null);
  }, [partyId]);

  if (!Number.isFinite(partyId)) {
    return (
      <AppShell title="Bad party id">
        <Card>
          <ErrorBanner>Not a valid party id.</ErrorBanner>
        </Card>
      </AppShell>
    );
  }

  const partyName = party.data?.displayName ?? '…';
  const currentBalance = balance.data?.balance ?? '…';

  const pdfUrl = `/api/parties/${partyId}/ledger.pdf?from=${from}&to=${to}&include_reversed=${includeReversed}`;
  const pngUrl = `/api/parties/${partyId}/ledger.png?from=${from}&to=${to}&include_reversed=${includeReversed}`;
  const pngFileName = `ledger-${partyId}-${from}-${to}.png`;

  return (
    <AppShell
      title={`Ledger — ${partyName}`}
      toolbar={
        <PageToolbar
          back={{ to: '/ledgers', label: 'All ledgers' }}
          title={`Ledger — ${partyName}`}
          primary={
            !readOnly ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Btn size="sm" onClick={() => setPaymentOpen(true)}>
                  <ArrowDownToLine className="h-3.5 w-3.5" strokeWidth={2} />
                  <span className="hidden sm:inline">Payment</span>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAdjustmentOpen(true)}>
                  <PencilRuler className="h-3.5 w-3.5" strokeWidth={2} />
                  <span className="hidden sm:inline">Adjustment</span>
                </Btn>
              </div>
            ) : undefined
          }
          overflow={[
            {
              label: 'Edit party…',
              icon: Pencil,
              onClick: () => setEditPartyOpen(true),
              disabled: !party.data || readOnly,
            },
            { separator: true, label: '' },
            {
              label: 'Download PDF',
              icon: FileText,
              href: pdfUrl,
              target: '_blank',
            },
            {
              label: 'Download PNG',
              icon: ImageIcon,
              href: pngUrl,
              download: pngFileName,
            },
          ]}
        />
      }
    >
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {error && <ErrorBanner>{error}</ErrorBanner>}

          {/* Date range + toggle */}
          <Card>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-small text-ink-muted">
              <label className="flex items-center gap-1.5">
                <span className="shrink-0 text-ink-muted">From:</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </label>
              <label className="flex items-center gap-1.5">
                <span className="shrink-0 text-ink-muted">To:</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </label>
              <Checkbox
                label="Show reversed entries"
                checked={includeReversed}
                onChange={setIncludeReversed}
              />
            </div>
          </Card>

          {/* Ledger table */}
          <Card>
            {ledger.isLoading ? (
              <div className="flex items-center gap-2 text-small text-ink-muted">
                <Spinner /> loading…
              </div>
            ) : ledger.data ? (
              <>
                {/* Desktop: full ledger table */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="min-w-full text-small">
                    <thead className="bg-surface-raised text-caption uppercase tracking-wide text-ink-muted">
                      <tr>
                        <th className="px-2 py-2 text-left">Date</th>
                        <th className="px-2 py-2 text-left">Description</th>
                        <th className="px-2 py-2 text-right">Dr.</th>
                        <th className="px-2 py-2 text-right">Cr.</th>
                        <th className="px-2 py-2 text-right">Balance</th>
                        <th className="px-2 py-2 text-left">Source</th>
                        <th className="px-2 py-2 text-left">Attach</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {/* Opening balance row */}
                      <tr className="bg-amber-50/60">
                        <td className="px-2 py-1.5 text-amber-700">{from}</td>
                        <td className="px-2 py-1.5 font-medium text-amber-700">Opening balance</td>
                        <td className="px-2 py-1.5" />
                        <td className="px-2 py-1.5" />
                        <td className="px-2 py-1.5 text-right font-mono font-semibold text-amber-700">
                          {ledger.data.openingBalanceAsOfFrom}
                        </td>
                        <td className="px-2 py-1.5" />
                        <td className="px-2 py-1.5" />
                        <td className="px-2 py-1.5" />
                      </tr>
                      {ledger.data.rows.map((row) => (
                        <LedgerRowView
                          key={row.id}
                          row={row}
                          attachments={attachmentsByEntry.get(row.id) ?? []}
                          readOnly={readOnly}
                          onOpenAttachment={(entryId) =>
                            setViewer({ entryId, index: 0 })
                          }
                          onAttachFile={(entryId, file) =>
                            uploadAttachmentMut.mutate({ entryId, file })
                          }
                          uploadingEntryId={
                            uploadAttachmentMut.isPending
                              ? uploadAttachmentMut.variables?.entryId ?? null
                              : null
                          }
                          onReverse={(entryId, reason) =>
                            void withPin(() =>
                              reverseEntryMut.mutateAsync({ entryId, reason }),
                            )
                          }
                          onVoidPayment={(paymentId, reason) =>
                            void withPin(() =>
                              voidPayMut.mutateAsync({ paymentId, reason }),
                            )
                          }
                        />
                      ))}
                      {ledger.data.rows.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-2 py-4 text-center text-small text-ink-muted">
                            No entries in this range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border bg-surface-raised">
                        <td className="px-2 py-2 font-semibold text-ink" colSpan={4}>
                          Closing balance ({to})
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-semibold text-ink">
                          {ledger.data.closingBalance}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile: ledger entry cards */}
                <div className="space-y-2 md:hidden">
                  {/* Opening balance card */}
                  <div className="rounded-md border border-amber-200/60 bg-amber-50/60 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-small font-semibold text-amber-700">Opening balance</span>
                      <span className="font-mono font-semibold text-amber-700">
                        {ledger.data.openingBalanceAsOfFrom}
                      </span>
                    </div>
                    <p className="mt-0.5 text-caption text-amber-600/70">{from}</p>
                  </div>

                  {/* Entry cards */}
                  {ledger.data.rows.map((row) => (
                    <LedgerEntryMobileCard key={row.id} row={row} />
                  ))}

                  {ledger.data.rows.length === 0 && (
                    <p className="py-4 text-center text-small text-ink-muted">
                      No entries in this range.
                    </p>
                  )}

                  {/* Closing balance card */}
                  <div className="rounded-md border border-border bg-surface-raised p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-small font-semibold text-ink">
                        Closing balance ({to})
                      </span>
                      <span className="font-mono font-semibold text-ink">
                        {ledger.data.closingBalance}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <EmptyState
                icon={BookOpen}
                title="No ledger data"
                description="Adjust the date range above."
              />
            )}
          </Card>
        </div>

        {/* ── Sidebar ────────────────────────────────────────────────────── */}
        <aside className="space-y-4">
          <Card>
            <h3 className="mb-2 text-small font-semibold text-ink">Current balance</h3>
            <p className={`font-mono text-display font-bold tabular-nums ${toneFromBalance(currentBalance)}`}>
              {currentBalance}
            </p>
            <p className="mt-1 text-caption text-ink-muted">
              K-INR. Positive = party owes you; negative = you owe party.
            </p>
          </Card>
          <Card>
            <h3 className="mb-2 text-small font-semibold text-ink">Role</h3>
            <StatePill state={party.data?.role ?? '—'} />
          </Card>
        </aside>
      </div>

      {paymentOpen && (
        <PaymentDialog
          preselectPayeeId={partyId}
          onClose={() => setPaymentOpen(false)}
          onCreated={() => setPaymentOpen(false)}
        />
      )}

      {adjustmentOpen && (
        <AdjustmentDialog
          preselectDrPartyId={partyId}
          onClose={() => setAdjustmentOpen(false)}
          onCreated={() => setAdjustmentOpen(false)}
        />
      )}

      {editPartyOpen && party.data && (
        <EditPartyDialog
          party={party.data}
          onClose={() => setEditPartyOpen(false)}
          onSaved={() => setEditPartyOpen(false)}
        />
      )}

      {viewer && (
        <AttachmentViewer
          entryId={viewer.entryId}
          attachments={attachmentsByEntry.get(viewer.entryId) ?? []}
          index={viewer.index}
          readOnly={readOnly}
          onIndexChange={(index) => setViewer({ ...viewer, index })}
          onClose={() => setViewer(null)}
          onAddFile={(entryId, file) =>
            uploadAttachmentMut.mutate({ entryId, file })
          }
          onDeleteAttachment={(attachmentId) => {
            const list = attachmentsByEntry.get(viewer.entryId) ?? [];
            deleteAttachmentMut.mutate(attachmentId);
            if (list.length <= 1) {
              setViewer(null);
            } else {
              setViewer({
                entryId: viewer.entryId,
                index: Math.max(0, viewer.index - (viewer.index === list.length - 1 ? 1 : 0)),
              });
            }
          }}
        />
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function LedgerEntryMobileCard({ row }: { row: LedgerListingRow }) {
  const balanceNum = parseFloat(String(row.runningBalance).replace(/,/g, ''));
  const balancePositive = !Number.isNaN(balanceNum) && balanceNum >= 0;
  const isReversal = row.reversesEntryId !== null;
  const hasDr = row.dr !== '0';
  const hasCr = row.cr !== '0';
  return (
    <div
      className={`rounded-md border p-3 text-small ${
        isReversal ? 'border-border/40 opacity-60' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-ink">{row.effectiveDate}</span>
        <span
          className={`font-mono font-semibold ${
            balancePositive ? 'text-ink' : 'text-neg'
          }`}
        >
          {row.runningBalance}
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-caption text-ink-muted">{row.description}</p>
      {(hasDr || hasCr) && (
        <div className="mt-1.5 flex gap-4 text-caption">
          {hasDr && (
            <span>
              <span className="text-ink-muted">Dr </span>
              <span className="font-mono text-ink">{row.dr}</span>
            </span>
          )}
          {hasCr && (
            <span>
              <span className="text-warn">Cr </span>
              <span className="font-mono text-warn">{row.cr}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function LedgerRowView({
  row,
  attachments,
  readOnly,
  onOpenAttachment,
  onAttachFile,
  uploadingEntryId,
  onReverse,
  onVoidPayment,
}: {
  row: LedgerListingRow;
  attachments: EntryAttachment[];
  readOnly: boolean;
  onOpenAttachment: (entryId: number) => void;
  onAttachFile: (entryId: number, file: File) => void;
  uploadingEntryId: number | null;
  onReverse: (entryId: number, reason: string) => void;
  onVoidPayment: (paymentId: number, reason: string) => void;
}) {
  const source = sourceLabel(row);
  const isReversal = row.reversesEntryId !== null;
  const isUploading = uploadingEntryId === row.id;
  return (
    <tr className={isReversal ? 'text-ink-muted' : 'text-ink'}>
      <td className="px-2 py-1.5">{row.effectiveDate}</td>
      <td className="px-2 py-1.5">{row.description}</td>
      <td className="px-2 py-1.5 text-right font-mono">
        {row.dr === '0' ? '' : row.dr}
      </td>
      <td className="px-2 py-1.5 text-right font-mono text-warn">
        {row.cr === '0' ? '' : `-${row.cr}`}
      </td>
      <td className="px-2 py-1.5 text-right font-mono">{row.runningBalance}</td>
      <td className="px-2 py-1.5">
        <span className="inline-flex items-center rounded-full bg-surface-raised px-2 py-0.5 text-caption font-medium text-ink-muted">
          {source}
        </span>
      </td>
      <td className="px-2 py-1.5">
        {attachments.length > 0 ? (
          <button
            type="button"
            className="text-caption text-brand hover:underline"
            onClick={() => onOpenAttachment(row.id)}
          >
            {attachments.length} file{attachments.length > 1 ? 's' : ''}
          </button>
        ) : (
          <AttachFileButton
            disabled={isReversal}
            uploading={isUploading}
            onFile={(file) => onAttachFile(row.id, file)}
          />
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {!readOnly && !isReversal && row.sourcePaymentId !== null && (
          <button
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-neg transition-colors hover:bg-neg/5"
            onClick={() => {
              const reason = prompt('Reason for voiding this payment?');
              if (reason && reason.trim().length > 0) {
                onVoidPayment(row.sourcePaymentId!, reason.trim());
              }
            }}
          >
            <Undo2 className="h-3.5 w-3.5" strokeWidth={2} />
            Void
          </button>
        )}
        {!readOnly &&
          !isReversal &&
          row.sourcePaymentId === null &&
          row.sourceSettlementId === null && (
            <button
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-neg transition-colors hover:bg-neg/5"
              onClick={() => {
                const reason = prompt('Reason for reversing this entry?');
                if (reason && reason.trim().length > 0) {
                  onReverse(row.id, reason.trim());
                }
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
              Reverse
            </button>
          )}
      </td>
    </tr>
  );
}

function sourceLabel(row: LedgerListingRow): string {
  if (row.reversesEntryId !== null) return 'REVERSAL';
  if (row.sourceBatchId !== null) return 'BATCH';
  if (row.sourcePaymentId !== null) return 'PAYMENT';
  if (row.sourceAdjustmentId !== null) return 'ADJUSTMENT';
  return row.type;
}

function AttachFileButton({
  onFile,
  uploading,
  disabled,
}: {
  onFile: (file: File) => void;
  uploading: boolean;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  if (disabled) return <span className="text-caption text-ink-muted">—</span>;
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <button
        type="button"
        disabled={uploading}
        title="Attach a receipt/image to this entry"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-caption text-ink-muted transition-colors hover:border-brand/60 hover:bg-surface-raised hover:text-brand disabled:cursor-wait disabled:opacity-60"
      >
        {uploading ? (
          <Spinner />
        ) : (
          <>
            <Paperclip className="h-3 w-3" strokeWidth={2} />
            <span>+ Add</span>
          </>
        )}
      </button>
    </>
  );
}

function AttachmentViewer({
  entryId,
  attachments,
  index,
  readOnly,
  onIndexChange,
  onClose,
  onAddFile,
  onDeleteAttachment,
}: {
  entryId: number;
  attachments: EntryAttachment[];
  index: number;
  readOnly: boolean;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onAddFile: (entryId: number, file: File) => void;
  onDeleteAttachment: (attachmentId: number) => void;
}) {
  const addFileInputRef = useRef<HTMLInputElement | null>(null);
  if (attachments.length === 0) return null;
  const idx = Math.max(0, Math.min(index, attachments.length - 1));
  const a = attachments[idx]!;
  const url = `/api/attachments/${a.sha256}`;
  const isImage = a.mimeType.startsWith('image/');
  const isPdf = a.mimeType === 'application/pdf';
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-4xl flex-col items-center gap-3 rounded-card border border-border bg-surface p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full items-center justify-between">
          <div className="text-small">
            <span className="font-medium text-ink">{a.originalFilename}</span>
            <span className="ml-2 text-caption text-ink-muted">
              {(a.byteSize / 1024).toFixed(0)} kB · {a.mimeType}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="max-h-[75vh] w-full overflow-auto rounded-md border border-border bg-surface-raised">
          {isImage ? (
            <img
              src={url}
              alt={a.originalFilename}
              className="mx-auto block max-h-[70vh] max-w-full"
            />
          ) : isPdf ? (
            <embed
              src={url}
              type="application/pdf"
              className="h-[70vh] w-full"
            />
          ) : (
            <div className="p-8 text-center text-small text-ink-muted">
              <p>Preview not supported for this file type.</p>
              <a
                href={url}
                download={a.originalFilename}
                className="mt-3 inline-block rounded-md border border-border px-3 py-2 text-ink hover:bg-surface-raised"
              >
                Download
              </a>
            </div>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {attachments.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onIndexChange(Math.max(0, idx - 1))}
                  disabled={idx === 0}
                >
                  ← Prev
                </Button>
                <span className="text-caption text-ink-muted">
                  {idx + 1} / {attachments.length}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onIndexChange(Math.min(attachments.length - 1, idx + 1))}
                  disabled={idx === attachments.length - 1}
                >
                  Next →
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <>
                <input
                  ref={addFileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAddFile(entryId, f);
                    if (addFileInputRef.current) addFileInputRef.current.value = '';
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => addFileInputRef.current?.click()}
                >
                  + Add more
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete "${a.originalFilename}"? This cannot be undone.`)) {
                      onDeleteAttachment(a.id);
                    }
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function toneFromBalance(v: string): string {
  if (v === '…' || v === '0') return 'text-ink-muted';
  if (v.startsWith('-')) return 'text-neg';
  return 'text-pos';
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(isoDate: string, delta: number): string {
  const [y, m, d] = isoDate.split('-').map((s) => parseInt(s, 10));
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  date.setUTCDate(date.getUTCDate() + delta);
  const yy = date.getUTCFullYear().toString().padStart(4, '0');
  const mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = date.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
